import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import {FullSideModalLayout} from 'components/Modal/SideModalLayout'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React from 'react'
import {NullAsDefault, renderIf} from 'views/Shared'
import {CodiActions} from './codiHelpers'
import ArrangeContent from './ArrangeContent'
import CodiContext from './CodiContext'
import CodiShortcuts from './CodiShortcuts'
import {
    CodiBrosCodiBlock,
    CodiDetailBlock,
    CodiIndexesBlock,
    CodiInfoBlock,
    CodiKeywordsBlock,
    CodiLinksBlock,
    CodiNotesBlock,
    CodiRelatedArticlesBlock,
    CodiRelatedCodiBlock,
    CodiSearchBlock,
    CodiSettingsBlock,
} from './CodiSider'
import {EditContent, InsertContent} from './ModifyContent'
import {RowSettingProvider} from './RowSettingContext'
import UpdateRowType from './UpdateRowType'

const ModalLayout = ({title, children, ...props}) => {
  const t = useTranslate()
  return (
    <FullSideModalLayout
      center={true}
      title={
        _.isString(title) ? (
          <span className="max-lines-2 font-medium text-sm text-color-000">
            {t(title)}
          </span>
        ) : (
          title
        )
      }
      {...props}>
      {children}
    </FullSideModalLayout>
  )
}

const createCodiModal = (
  Component = NullAsDefault,
  {modal, row_required = false, withContext = (props) => ({}), ...props} = {}
) => (
  <CodiContext.Consumer>
    {(context) => {
      const {item, modalDismissRef, setCurrent = NullAsDefault} = context ?? {}
      const onCancel = () => {
        setCurrent(null)
        if (_.isFunction(modalDismissRef?.current)) {
          modalDismissRef.current()
        }
      }
      const Wrapper = row_required ? RowSettingProvider : React.Fragment
      return (
        <ModalLayout
          footer={null}
          title={
            <span className="text-primary text-base font-semibold truncate">
              {getTitle(item)}
            </span>
          }
          {...(modal ?? {})}
          onCancel={onCancel}>
          <Wrapper>
            <LayoutContext.Consumer>
              {({isSm}) => renderIf(isSm, <CodiShortcuts/>)}
            </LayoutContext.Consumer>
            <Component
              onCancel={onCancel}
              {...(props ?? {})}
              {...(withContext({onCancel, ...(context ?? {})}) ?? {})}
            />
          </Wrapper>
        </ModalLayout>
      )
    }}
  </CodiContext.Consumer>
)

export const routes = {
  info: {
    path: 'info',
    name: 'info',
    action: CodiActions.SHOW_INFO_MODAL,
    component: () => createCodiModal(CodiInfoBlock),
    exact: true,
  },
  indexes: {
    path: 'indexes',
    name: 'indexes',
    action: CodiActions.SHOW_INDEXES_MODAL,
    component: () => createCodiModal(CodiIndexesBlock),
    exact: true,
  },
  detail: {
    path: 'detail',
    name: 'detail',
    action: CodiActions.SHOW_DETAIL_MODAL,
    component: () => createCodiModal(CodiDetailBlock),
    exact: true,
  },
  notes: {
    path: 'notes',
    name: 'notes',
    action: CodiActions.SHOW_NOTES_MODAL,
    component: () =>
      createCodiModal(CodiNotesBlock, {
        withContext: ({current_row}) => ({
          current_row,
        }),
      }),
    exact: true,
  },
  links: {
    path: 'links',
    name: 'links',
    action: CodiActions.SHOW_LINKS_MODAL,
    component: () =>
      createCodiModal(CodiLinksBlock, {
        withContext: ({current_row, filterActions}) => ({
          current_row,
          filterActions,
        }),
      }),
    exact: true,
  },
  keywords: {
    path: 'keywords',
    name: 'keywords',
    action: CodiActions.SHOW_KEYWORDS_MODAL,
    component: () =>
      createCodiModal(CodiKeywordsBlock, {
        withContext: ({current_row}) => ({
          current_row,
        }),
      }),
    exact: true,
  },
  search: {
    path: 'search',
    name: 'search',
    action: CodiActions.SHOW_SEARCH_MODAL,
    component: () => createCodiModal(CodiSearchBlock),
    exact: true,
  },
  relatedCodi: {
    path: 'related-codi',
    name: 'related-codi',
    action: CodiActions.SHOW_RELATED_CODI_MODAL,
    component: () => createCodiModal(CodiRelatedCodiBlock),
    exact: true,
  },
  brosCodi: {
    path: 'bros-codi',
    name: 'bros-codi',
    action: CodiActions.SHOW_RELATED_CODI_MODAL,
    component: () => createCodiModal(CodiBrosCodiBlock),
    exact: true,
  },
  relatedArticles: {
    path: 'related-articles',
    name: 'related-articles',
    action: CodiActions.SHOW_RELATED_ARTICLES_MODAL,
    component: () => createCodiModal(CodiRelatedArticlesBlock),
    exact: true,
  },
  settings: {
    path: 'settings',
    name: 'settings',
    action: CodiActions.SHOW_ADMIN_MODAL,
    component: () => createCodiModal(CodiSettingsBlock),
    exact: true,
  },
  insertContent: {
    path: 'insert-content',
    name: 'insert-content',
    action: CodiActions.SHOW_INSERT_CONTENT_MODAL,
    component: () =>
      createCodiModal(InsertContent, {
        row_required: true,
        modal: {title: null},
        withContext: ({onCancel = NullAsDefault, onRefresh = NullAsDefault}) => ({
          onSuccess: () => {
            onCancel()
            onRefresh()
          },
        }),
      }),
    exact: true,
  },
  editContent: {
    path: 'edit-content',
    name: 'edit-content',
    action: CodiActions.SHOW_EDIT_CONTENT_MODAL,
    component: () =>
      createCodiModal(EditContent, {
        row_required: true,
        modal: {title: null},
        withContext: ({onCancel = NullAsDefault, onRefresh = NullAsDefault}) => ({
          onSuccess: () => {
            onCancel()
            onRefresh()
          },
        }),
      }),
    exact: true,
  },
  arrangeContent: {
    path: 'arrange-content',
    name: 'arrange-content',
    action: CodiActions.SHOW_REARRANGE_CONTENT_MODAL,
    component: () =>
      createCodiModal(ArrangeContent, {
        row_required: true,
        modal: {title: null},
        withContext: ({onCancel = NullAsDefault, onRefresh = NullAsDefault}) => ({
          onSuccess: () => {
            onCancel()
            onRefresh()
          },
        }),
      }),
    exact: true,
  },
  updateRowType: {
    path: 'update-row-type',
    name: 'update-row-type',
    action: CodiActions.SHOW_UPDATE_ROW_TYPE_MODAL,
    component: () =>
      createCodiModal(UpdateRowType, {
        row_required: true,
        modal: {title: null},
        withContext: ({onCancel = NullAsDefault, onRefresh = NullAsDefault}) => ({
          onSuccess: () => {
            onCancel()
            onRefresh()
          },
        }),
      }),
    exact: true,
  },
}

export const getActionRoute = (action) => _.find(routes, {action})

export const settingRoutes = {}
